import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import type { Asset, EpisodeDetail, EpisodeStatus, PipelineJob, SideJob, TranscriptStatus, Workspace } from '../api/types'
import { FileDrop } from '../components/FileDrop'
import { Modal } from '../components/Modal'
import { Player, type PlayerHandle } from '../components/Player'
import { ListenersCard } from '../components/ListenersCard'
import { PublishCard } from '../components/PublishCard'
import { SoundCard } from '../components/SoundCard'
import { EpisodeStatusBadge, JobStatusBadge } from '../components/StatusBadges'
import { ClipsPanel } from '../components/ClipsPanel'
import { TextPanel } from '../components/TextPanel'
import { TranscriptView } from '../components/TranscriptView'
import { UploadProgress } from '../components/UploadProgress'
import { Badge, Button, Card, EmptyState, ErrorNote, Field, LoadingBlock, PageHeader, ProgressBar } from '../components/ui'
import { refreshEpisode, useDeleteEpisode, useEpisode, useEpisodeLive, useReprocessEpisode, useUpdateEpisode, useWaveform } from '../hooks/useShows'
import { Cost } from '../components/Cost'
import { useEpisodeCosts } from '../hooks/useBilling'
import { useEpisodeStats } from '../hooks/useStats'
import { useCreateClip, useMakeThumbnail } from '../hooks/useClips'
import { captionsUrl, useEditSegment, useRenameSpeaker, useTranscribe, useTranscript } from '../hooks/useTranscript'
import { useCurrentWorkspace } from '../hooks/useWorkspace'
import { cancelUpload, dismissUpload, isActive, startUpload, useUpload } from '../uploads/store'
import { elapsed, formatBytes, formatDuration, languageName } from '../utils/format'

const STEP_LABELS: Record<string, string> = {
  'media:probe': 'Inspect the recording',
  'media:transcode': 'Make playback files',
  'audio:transcribe': 'Transcribe',
}

const STAGE_LABELS: Record<string, string> = {
  download: 'Fetching the recording',
  audio: 'Encoding audio',
  proxy: 'Encoding 720p video',
  poster: 'Picking a poster frame',
  waveform: 'Drawing the waveform',
  upload: 'Saving files',
  submit: 'Sending the audio to fal',
  queued: 'Waiting in the transcription queue',
  transcribing: 'Transcribing',
}

const ASSET_LABELS: Record<string, string> = {
  source: 'Original recording',
  audio_mp3: 'Audio (MP3)',
  audio_opus: 'Audio (Opus)',
  audio_enhanced: 'Audio, cleaned up',
  proxy: 'Video (720p)',
  waveform: 'Waveform',
  poster: 'Poster frame',
  artwork: 'Artwork',
  thumbnail: 'Thumbnail',
}

export function EpisodePage() {
  const { episodeId = '' } = useParams()
  const { workspace } = useCurrentWorkspace()
  if (!workspace) return <Navigate to="/studio" replace />
  return <EpisodeView workspace={workspace} episodeId={episodeId} />
}

function EpisodeView({ workspace, episodeId }: { workspace: Workspace; episodeId: string }) {
  const ws = workspace.id
  const episode = useEpisode(ws, episodeId)
  const live = useEpisodeLive(ws, episodeId)
  const waveform = useWaveform(ws, episodeId, !!episode.data?.playback.waveform)
  const stats = useEpisodeStats(ws, episodeId)
  const reprocess = useReprocessEpisode(ws, episodeId)
  const [editing, setEditing] = useState(false)
  const player = useRef<PlayerHandle>(null)
  const [currentMs, setCurrentMs] = useState(0)
  const seek = useCallback((ms: number) => player.current?.seek(ms), [])
  const canEdit = workspace.role !== 'viewer'

  if (episode.isPending) return <LoadingBlock />
  if (episode.isError) {
    return (
      <div className="stack">
        <ErrorNote error={episode.error} />
        <Link to="/studio">Back to your shows</Link>
      </div>
    )
  }
  const detail = episode.data
  const status: EpisodeStatus = live?.status ?? detail.status
  const jobs = live?.jobs ?? detail.jobs
  const error = live?.error ?? detail.error
  const medium = live?.medium ?? detail.medium
  const duration = live?.duration_ms ?? detail.duration_ms
  const transcriptStatus: TranscriptStatus = live?.transcript_status ?? detail.transcript_status
  const canReprocess = canEdit && !!detail.source && (status === 'ready' || status === 'failed')

  return (
    <>
      <div className="breadcrumbs"><Link to={`/studio/shows/${detail.show_id}`}>{detail.show_title}</Link></div>
      <PageHeader
        title={detail.title}
        subtitle={
          <span className="row">
            <EpisodeStatusBadge status={status} />
            {medium && <Badge>{medium}</Badge>}
            {duration != null && <span className="muted">{formatDuration(duration)}</span>}
          </span>
        }
        actions={
          canEdit && (
            <>
              {canReprocess && (
                <Button variant="secondary" busy={reprocess.isPending} onClick={() => reprocess.mutate()}>Process again</Button>
              )}
              <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
            </>
          )
        }
      />
      {status === 'failed' && error && <ErrorNote message={`Processing failed: ${error}`} />}
      {reprocess.error && <ErrorNote error={reprocess.error} />}
      <div className="episode-layout">
        <div className="stack-lg">
          <Card title="Player">
            {detail.playback.src ? (
              <div className="stack-sm">
                <Player ref={player} playback={detail.playback} waveform={waveform.data} onTime={setCurrentMs} />
                {!detail.playback.transcoded && (
                  <span className="muted small">Playing the original upload until the playback files are ready.</span>
                )}
              </div>
            ) : (
              <EmptyState title="Nothing to play yet" body="Upload a recording and it plays here." />
            )}
          </Card>
          <TranscriptPanel
            workspaceId={ws}
            episodeId={episodeId}
            status={transcriptStatus}
            error={detail.transcript_error}
            playable={status === 'ready' || status === 'published'}
            job={jobs.find((j) => j.job_type === 'audio:transcribe')}
            canEdit={canEdit}
            currentMs={currentMs}
            onSeek={seek}
            show={{ id: detail.show_id, language: detail.show_language }}
          />
          <TextPanel workspaceId={ws} episodeId={episodeId} transcriptReady={transcriptStatus === 'ready'} canEdit={canEdit} onSeek={seek} />
          <ClipsPanel workspaceId={ws} episodeId={episodeId} transcriptReady={transcriptStatus === 'ready'} canEdit={canEdit} onSeek={seek} />
          <Card title="Recording">
            <RecordingPanel workspaceId={ws} episode={detail} canEdit={canEdit} />
          </Card>
          <SoundCard workspaceId={ws} episodeId={episodeId} canEdit={canEdit} />
          {detail.description && (
            <Card title="Description">
              <p className="prewrap">{detail.description}</p>
            </Card>
          )}
        </div>
        <div className="stack-lg">
          <Card title="Processing">
            <PipelineSteps status={status} jobs={jobs} />
          </Card>
          <PublishCard workspaceId={ws} episode={detail} status={status} canEdit={canEdit} />
          {detail.published_at && <ListenersCard query={stats} />}
          <ThumbnailCard
            workspaceId={ws}
            episodeId={episodeId}
            thumbnail={detail.assets.find((a) => a.kind === 'thumbnail')}
            job={live?.side_jobs.find((j) => j.job_type === 'image:thumbnail')}
            canEdit={canEdit}
            playable={status === 'ready' || status === 'published'}
          />
          <Card title="Files">
            <AssetList assets={detail.assets} />
          </Card>
        </div>
      </div>
      <Modal open={editing} title="Edit episode" onClose={() => setEditing(false)} wide>
        <EpisodeForm workspaceId={ws} episode={detail} onDone={() => setEditing(false)} />
      </Modal>
    </>
  )
}

function RecordingPanel({ workspaceId, episode, canEdit }: { workspaceId: string; episode: EpisodeDetail; canEdit: boolean }) {
  const upload = useUpload(episode.id)
  const queryClient = useQueryClient()
  const [replacing, setReplacing] = useState(false)
  const start = (file: File) => {
    setReplacing(false)
    void startUpload(workspaceId, episode.id, file, () => refreshEpisode(queryClient, workspaceId, episode.id))
  }

  if (upload && (isActive(upload) || upload.phase === 'failed' || upload.phase === 'cancelled')) {
    return (
      <div className="stack">
        <UploadProgress entry={upload} onCancel={() => cancelUpload(episode.id)} />
        {!isActive(upload) && canEdit && (
          <div className="stack-sm">
            <FileDrop onFile={start} prompt="Try again: drop the recording here" />
            <div><Button variant="ghost" onClick={() => dismissUpload(episode.id)}>Dismiss</Button></div>
          </div>
        )}
      </div>
    )
  }
  if (!episode.source) {
    if (!canEdit) return <EmptyState title="No recording yet" />
    return (
      <div className="stack-sm">
        {episode.upload && (
          <div className="notice small">An earlier upload did not finish. Choose the file again to start over.</div>
        )}
        <FileDrop onFile={start} />
      </div>
    )
  }
  return (
    <div className="stack">
      <SourceDetails asset={episode.source} />
      {canEdit && (replacing ? <FileDrop onFile={start} prompt="Drop the replacement recording" /> : (
        <div><Button variant="secondary" onClick={() => setReplacing(true)}>Replace recording</Button></div>
      ))}
    </div>
  )
}

function SourceDetails({ asset }: { asset: Asset }) {
  const probe = asset.probe
  return (
    <dl className="kv">
      <dt>File</dt>
      <dd>{asset.original_filename ?? '—'}</dd>
      <dt>Size</dt>
      <dd>{formatBytes(asset.bytes)}</dd>
      <dt>Length</dt>
      <dd>{formatDuration(asset.duration_ms)}</dd>
      {probe?.video && (
        <>
          <dt>Video</dt>
          <dd>
            {probe.video.codec ?? '?'} · {probe.video.width}×{probe.video.height}
            {probe.video.frame_rate ? ` · ${probe.video.frame_rate} fps` : ''}
          </dd>
        </>
      )}
      {probe?.audio && (
        <>
          <dt>Audio</dt>
          <dd>
            {probe.audio.codec ?? '?'} · {probe.audio.sample_rate ? `${probe.audio.sample_rate / 1000} kHz` : '?'} · {probe.audio.channels ?? '?'} ch
          </dd>
        </>
      )}
      {!probe && (
        <>
          <dt>Details</dt>
          <dd className="muted">Available once the recording is inspected.</dd>
        </>
      )}
    </dl>
  )
}

function PipelineSteps({ status, jobs }: { status: EpisodeStatus; jobs: PipelineJob[] }) {
  if (jobs.length === 0) {
    return (
      <p className="muted">
        {status === 'uploading' ? 'Processing starts when the upload finishes.' : 'Processing starts once a recording is uploaded.'}
      </p>
    )
  }
  return (
    <div className="stack">
      {jobs.map((job) => (
        <PipelineStep key={job.id} job={job} />
      ))}
      {status === 'processing' && jobs.every((j) => j.status === 'succeeded') && (
        <span className="muted small">Starting the next step…</span>
      )}
    </div>
  )
}

function PipelineStep({ job }: { job: PipelineJob }) {
  const percent = job.status === 'succeeded' ? 100 : (job.progress?.percent ?? 0)
  const stage = job.progress?.stage
  return (
    <div className="stack-sm" data-testid={`step-${job.job_type}`}>
      <div className="row between">
        <strong>{STEP_LABELS[job.job_type] ?? job.job_type}</strong>
        <JobStatusBadge status={job.status} />
      </div>
      {job.status === 'leased' && (
        <>
          <ProgressBar value={percent} label={STEP_LABELS[job.job_type]} />
          <span className="muted small">
            {(stage && STAGE_LABELS[stage]) ?? 'Working'} · {Math.floor(percent)}%
          </span>
        </>
      )}
      {job.status === 'ready' && job.attempts > 0 && (
        <span className="muted small">Retrying: attempt {job.attempts + 1} of {job.max_attempts}.</span>
      )}
      {job.status === 'succeeded' && <span className="muted small">Done in {elapsed(job.started_at, job.completed_at)}.</span>}
      {job.error && job.status !== 'succeeded' && <span className="small error-text">{job.error}</span>}
    </div>
  )
}

function AssetList({ assets }: { assets: Asset[] }) {
  if (assets.length === 0) return <p className="muted">Files appear here as they are made.</p>
  return (
    <ul className="asset-list">
      {assets.map((asset) => (
        <li key={asset.id}>
          <div>
            <div>{ASSET_LABELS[asset.kind] ?? asset.kind}</div>
            <div className="muted small">
              {formatBytes(asset.bytes)}
              {asset.height ? ` · ${asset.width}×${asset.height}` : ''}
              {asset.duration_ms && asset.kind !== 'poster' ? ` · ${formatDuration(asset.duration_ms)}` : ''}
            </div>
          </div>
          <span className="row">
            {asset.url && (
              <a href={asset.url} target="_blank" rel="noreferrer" className="small">Open</a>
            )}
            {asset.download_url && (
              <a href={asset.download_url} download className="small">Download</a>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}

function EpisodeForm({ workspaceId, episode, onDone }: { workspaceId: string; episode: EpisodeDetail; onDone: () => void }) {
  const [title, setTitle] = useState(episode.title)
  const [description, setDescription] = useState(episode.description)
  const update = useUpdateEpisode(workspaceId, episode.id)
  const remove = useDeleteEpisode(workspaceId, episode.id)
  const navigate = useNavigate()
  return (
    <div className="stack-lg">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          update.mutate({ title: title.trim(), description: description.trim() }, { onSuccess: onDone })
        }}
      >
        <Field label="Title">
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} required />
        </Field>
        <Field label="Description">
          <textarea rows={5} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={20000} />
        </Field>
        {update.error && <ErrorNote error={update.error} />}
        <div className="row">
          <Button type="submit" busy={update.isPending} disabled={!title.trim()}>Save</Button>
        </div>
      </form>
      <div className="danger-zone stack-sm">
        <strong>Delete this episode</strong>
        <span className="muted small">Deletes the recording and every file made from it.</span>
        {remove.error && <ErrorNote error={remove.error} />}
        <div>
          <Button
            variant="danger"
            busy={remove.isPending}
            onClick={() => {
              if (window.confirm(`Delete "${episode.title}"?`)) {
                remove.mutate(undefined, { onSuccess: () => navigate(`/studio/shows/${episode.show_id}`) })
              }
            }}
          >
            Delete episode
          </Button>
        </div>
      </div>
    </div>
  )
}

function TranscriptPanel({
  workspaceId,
  episodeId,
  status,
  error,
  playable,
  job,
  canEdit,
  currentMs,
  onSeek,
  show,
}: {
  workspaceId: string
  episodeId: string
  status: TranscriptStatus
  error: string | null
  playable: boolean
  job: PipelineJob | undefined
  canEdit: boolean
  currentMs: number
  onSeek: (ms: number) => void
  show: { id: string; language: string }
}) {
  const transcript = useTranscript(workspaceId, episodeId, status === 'ready')
  const transcribe = useTranscribe(workspaceId, episodeId)
  const costs = useEpisodeCosts(workspaceId, episodeId)
  const edit = useEditSegment(workspaceId, episodeId)
  const rename = useRenameSpeaker(workspaceId, episodeId)
  const createClip = useCreateClip(workspaceId, episodeId)
  const { mutateAsync: createClipAsync } = createClip
  const onMakeClip = useCallback((startMs: number, endMs: number) => createClipAsync({ start_ms: startMs, end_ms: endMs }), [createClipAsync])
  const baseVersion = transcript.data?.version
  const { mutateAsync: editAsync } = edit
  const { mutateAsync: renameAsync } = rename
  const onEditSegment = useCallback(
    async (segmentId: string, text: string) => {
      if (baseVersion != null) await editAsync({ segmentId, text, baseVersion })
    },
    [editAsync, baseVersion],
  )
  const onRenameSpeaker = useCallback(
    async (from: string, to: string) => {
      if (baseVersion != null) await renameAsync({ from, to, baseVersion })
    },
    [renameAsync, baseVersion],
  )
  const canTranscribe = canEdit && playable && status !== 'pending'
  return (
    <Card
      title="Transcript"
      actions={
        canTranscribe && (
          <Button variant="secondary" busy={transcribe.isPending} onClick={() => transcribe.mutate()}>
            {status === 'ready' ? 'Transcribe again' : 'Transcribe'} <Cost micro={costs.data?.transcribe} />
          </Button>
        )
      }
    >
      <div className="stack">
        {transcribe.error && <ErrorNote error={transcribe.error} />}
        {status === 'pending' && <TranscribeProgress job={job} />}
        {status === 'failed' && <ErrorNote message={`Transcription failed: ${error ?? 'no reason was given.'}`} />}
        {status === 'none' && (
          <p className="muted">
            {!playable
              ? 'The playback files are still being made; a transcript can be asked for once they are ready.'
              : canTranscribe
                ? 'No transcript yet. Transcribing costs credits, so it only starts when you ask.'
                : 'This episode has no transcript yet.'}
          </p>
        )}
        {status === 'ready' && transcript.data && <LanguageNote detected={transcript.data.language} show={show} />}
        {status === 'ready' &&
          (transcript.data ? (
            <TranscriptView
              version={transcript.data}
              currentMs={currentMs}
              onSeek={onSeek}
              canEdit={canEdit}
              captions={{ vtt: captionsUrl(workspaceId, episodeId, 'vtt'), srt: captionsUrl(workspaceId, episodeId, 'srt') }}
              onEditSegment={onEditSegment}
              onRenameSpeaker={onRenameSpeaker}
              saving={edit.isPending || rename.isPending}
              error={edit.error ?? rename.error ?? createClip.error}
              onMakeClip={canEdit ? onMakeClip : undefined}
            />
          ) : transcript.isError ? (
            <ErrorNote error={transcript.error} />
          ) : (
            <LoadingBlock />
          ))}
      </div>
    </Card>
  )
}

/** The show says one language and the recording turned out to be another: the
 *  feed and the directories would say the wrong thing. */
function LanguageNote({ detected, show }: { detected: string | null; show: { id: string; language: string } }) {
  if (!detected || languageName(detected) === languageName(show.language)) return null
  return (
    <p className="muted small">
      This episode was heard as <strong>{languageName(detected)}</strong>, and the show says it is in{' '}
      <strong>{languageName(show.language)}</strong>. Podcast apps go by the show.{' '}
      <Link to={`/studio/shows/${show.id}`}>Change the show's language</Link> if the recording is right.
    </p>
  )
}

function TranscribeProgress({ job }: { job: PipelineJob | undefined }) {
  if (!job || job.status === 'ready') return <p className="muted">Waiting for a worker to start transcribing…</p>
  if (job.status === 'succeeded') return <p className="muted">Saving the transcript…</p>
  const percent = job.progress?.percent ?? 0
  const stage = job.progress?.stage
  return (
    <div className="stack-sm">
      <ProgressBar value={percent} label="Transcription" />
      <span className="muted small">
        {(stage && STAGE_LABELS[stage]) ?? 'Transcribing'} · {Math.floor(percent)}%
      </span>
    </div>
  )
}

function ThumbnailCard({
  workspaceId,
  episodeId,
  thumbnail,
  job,
  canEdit,
  playable,
}: {
  workspaceId: string
  episodeId: string
  thumbnail: Asset | undefined
  job: SideJob | undefined
  canEdit: boolean
  playable: boolean
}) {
  const make = useMakeThumbnail(workspaceId, episodeId)
  const costs = useEpisodeCosts(workspaceId, episodeId)
  const busy = make.isPending || job?.status === 'ready' || job?.status === 'leased'
  return (
    <Card
      title="Thumbnail"
      actions={
        canEdit &&
        playable && (
          <Button variant="secondary" busy={busy} disabled={busy} onClick={() => make.mutate()}>
            {thumbnail ? 'Draw a new one' : 'Draw one'} <Cost micro={costs.data?.thumbnail} />
          </Button>
        )
      }
    >
      <div className="stack-sm">
        {thumbnail?.url ? (
          <img className="thumbnail" src={thumbnail.url} alt="Episode thumbnail" data-testid="thumbnail" />
        ) : (
          <p className="muted">{busy ? 'Drawing a thumbnail…' : canEdit ? 'No thumbnail yet. Draw one when you want it.' : 'No thumbnail yet.'}</p>
        )}
        {job?.status === 'failed' && <ErrorNote message={`The thumbnail failed: ${job.error ?? 'no reason was given.'}`} />}
        {make.error && <ErrorNote error={make.error} />}
      </div>
    </Card>
  )
}
